/**
 * TEMPORARY SCAFFOLDING
 *
 * These helpers exist to extract complex logic from Redux thunks during the
 * migration from Redux cellSelections to the new SelectionsApi/SelectionMachine.
 *
 * They will be DELETED (not moved) once the migration is complete because:
 * - Range calculations (1D/2D) are handled by the SelectionMachine reducers
 * - Highlights are rendered by effects subscribing to deltas
 * - Toggle logic is trivial enough to inline in components
 *
 * Do not build new features depending on these helpers.
 */

import { type SelectionsActions } from '@databases/selections-api-types';

export interface ToggleSelectionOptions {
  clear?: boolean;
  clearSelectedValues?: boolean;
}

/**
 * Handles toggle selection behavior: if the item is the only selected item,
 * deselect all. Otherwise, select just this item.
 */
export function toggleEntrySelection(
  selectionsActions: SelectionsActions,
  entryId: string,
  currentSelectedEntryIds: string[],
  options: ToggleSelectionOptions = {}
): void {
  const isOnlySelectedEntry =
    currentSelectedEntryIds.length === 1 && currentSelectedEntryIds[0] === entryId;

  if (isOnlySelectedEntry) {
    selectionsActions.deselectAll();
  } else {
    selectionsActions.selectEntries([entryId], options);
  }
}

/**
 * Handles toggle selection behavior for fields: if the field is the only selected field,
 * deselect all. Otherwise, select just this field.
 */
export function toggleFieldSelection(
  selectionsActions: SelectionsActions,
  fieldId: string,
  currentSelectedFieldIds: string[],
  options: ToggleSelectionOptions = {}
): void {
  const isOnlySelectedField =
    currentSelectedFieldIds.length === 1 && currentSelectedFieldIds[0] === fieldId;

  if (isOnlySelectedField) {
    selectionsActions.deselectAll();
  } else {
    selectionsActions.selectFields([fieldId], options);
  }
}

/**
 * Calculates a 1D range of IDs from a focused item to a target item.
 * Returns the slice of items between (and including) the focused and target indices.
 *
 * @param items - The ordered array of all item IDs
 * @param focusedId - The ID of the focused/anchor item
 * @param targetId - The ID of the target item (where user clicked)
 * @returns The range of IDs, or just the target if indices can't be determined
 */
export function calculateRangeFromFocus(
  items: string[],
  focusedId: string | undefined,
  targetId: string
): string[] {
  if (!focusedId) {
    return [targetId];
  }

  const focusedIndex = items.indexOf(focusedId);
  const targetIndex = items.indexOf(targetId);

  if (focusedIndex === -1 || targetIndex === -1) {
    return [targetId];
  }

  const startIndex = Math.min(focusedIndex, targetIndex);
  const endIndex = Math.max(focusedIndex, targetIndex);

  return items.slice(startIndex, endIndex + 1);
}

export interface SelectedValueCoordinate {
  fieldId: string;
  entryId: string;
}

/**
 * Calculates a 2D rectangular range of value coordinates given current selection
 * and a new target cell. The range extends from the min to max indices across
 * both entry and field dimensions.
 *
 * @param entries - The ordered array of all entry IDs
 * @param fields - The ordered array of all field IDs
 * @param currentSelectedEntryIds - Currently selected entry IDs
 * @param currentSelectedFieldIds - Currently selected field IDs
 * @param newEntryId - The new entry to extend selection to
 * @param newFieldId - The new field to extend selection to
 * @returns Array of value coordinates covering the rectangular selection
 */
export function calculate2DValueRange(
  entries: string[],
  fields: string[],
  currentSelectedEntryIds: string[],
  currentSelectedFieldIds: string[],
  newEntryId: string,
  newFieldId: string
): SelectedValueCoordinate[] {
  const selectedEntryIdxs = currentSelectedEntryIds.map((id) => entries.indexOf(id));
  const newEntryIdx = entries.indexOf(newEntryId);
  const rangeEntryIds = entries.slice(
    Math.min(...selectedEntryIdxs, newEntryIdx),
    Math.max(...selectedEntryIdxs, newEntryIdx) + 1
  );

  const selectedFieldIdxs = currentSelectedFieldIds.map((id) => fields.indexOf(id));
  const newFieldIdx = fields.indexOf(newFieldId);
  const rangeFieldIds = fields.slice(
    Math.min(...selectedFieldIdxs, newFieldIdx),
    Math.max(...selectedFieldIdxs, newFieldIdx) + 1
  );

  return rangeFieldIds.flatMap((fieldId) =>
    rangeEntryIds.map((entryId) => ({ fieldId, entryId }))
  );
}
